import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  fetchReviewQueue, fetchReview, lockReview, saveReviewDecision, fetchReviewFeedback, fetchReviewRules,
  downloadReviewCsv, fetchRefContent,
} from "../api";
import { SEVERITY_COLORS, METRIC_LABELS, METRIC_VALUE_ORDER, METRIC_VALUE_LABELS } from "../utils/cvss";
import { METRICS, parseVector, formatVector, baseScore, severity } from "../utils/cvss31";
import {
  POINTS, POINT_LABELS, VALUE_ORDER, VALUE_LABELS, OUTCOMES, OUTCOME_COLORS, outcome, parseSsvc, formatSsvc,
} from "../utils/ssvc";
import { fmtTs } from "../utils/time";

// type: "all" | "cvss" | "ssvc" — the queue's score type filter. sort/dir: a
// column key from review_db.SORTS ("" = the default priority order).
const DEFAULT_FILTERS = {
  type: "all", status: "unreviewed", priority: "", reason: "", cna: "", kernel: "", confidence: "", severity: "",
  outcome: "", q: "", sort: "", dir: "",
};
const PRIO_LABEL = { 1: "P1 must review", 2: "P2 review soon", 3: "P3 spot-check" };

function SeverityFromVector({ vector }) {
  return <SeverityBadge score={baseScore(parseVector(vector))} />;
}

function OutcomeBadge({ vector }) {
  const o = outcome(parseSsvc(vector));
  if (!o) return <span className="jobs-muted">—</span>;
  return <span className="review-sev" style={{ background: OUTCOME_COLORS[o] }}>{o}</span>;
}

// Everything that differs between the two score types. Reason codes are in
// priority order, as in libvinnie/review_rules.toml and ssvc_review_rules.toml.
const TYPES = {
  cvss: {
    key: "cvss", label: "CVSS 3.1", prefix: "CVSS:3.1/", unit: "vector", scoreLabel: "Score",
    components: METRICS, names: METRIC_LABELS, order: METRIC_VALUE_ORDER, values: METRIC_VALUE_LABELS,
    parse: parseVector, format: formatVector, Badge: SeverityFromVector,
    reasons: ["FORMAT_ERROR", "POST_STEP_DOWNGRADE", "CWE_VECTOR_CONFLICT", "TEXT_VECTOR_CONFLICT",
      "RATIONALE_CONFLICT", "CRITICAL", "LOW_CONF", "UNSTABLE", "SCOPE_CHANGED", "NETWORK_NOAUTH_FULL",
      "SPARSE_INPUT", "ASSUMED_HEAVY", "KERNEL_RULE_HOTSPOT", "KERNEL_CLASS_UPGRADE", "MISSING_FIELDS",
      "STEP0_MISMATCH", "RANDOM_AUDIT"],
  },
  ssvc: {
    key: "ssvc", label: "SSVC", prefix: "SSVCv2/", unit: "decision", scoreLabel: "Outcome",
    components: POINTS, names: POINT_LABELS, order: VALUE_ORDER, values: VALUE_LABELS,
    parse: parseSsvc, format: formatSsvc, Badge: OutcomeBadge,
    reasons: ["FORMAT_ERROR", "OUTCOME_MISMATCH", "EXPLOIT_EVIDENCE_CONFLICT", "AUTOMATABLE_CVSS_CONFLICT",
      "IMPACT_TEXT_CONFLICT", "RATIONALE_CONFLICT", "OUTCOME_ACT", "OUTCOME_ATTEND", "LOW_CONF", "UNSTABLE",
      "SPARSE_INPUT", "KERNEL_TOTAL_NO_EXPLOIT", "MISSING_FIELDS", "ECHOED_TEMPLATE", "RANDOM_AUDIT"],
  },
};

// Stored score_type → TYPES key.
const TYPE_OF = { "CVSSv3.1": "cvss", SSVC: "ssvc" };
const ALL_REASONS = [...new Set([...TYPES.cvss.reasons, ...TYPES.ssvc.reasons])];

// Queue: /review (all types), /review/cvss, /review/ssvc.
// Detail: /review/CVE-… (CVSS), /review/ssvc/CVE-….
function fromPath() {
  const m = window.location.pathname.match(/^\/review(?:\/(cvss|ssvc))?(?:\/(CVE-\d{4}-\d+))?/i);
  const t = m && m[1] ? m[1].toLowerCase() : null;
  const cve = m && m[2] ? m[2].toUpperCase() : null;
  return cve ? { type: t || "cvss", cve } : { type: t || "all", cve: null };
}

function reviewPath(type, cve) {
  if (cve) return "/review" + (type === "ssvc" ? "/ssvc" : "") + `/${cve}`;
  return "/review" + (type === "all" ? "" : `/${type}`);
}

// Scraped reference text: CRLF → LF, no trailing spaces, no runs of blank
// lines, and no blank lines between list items (they make a "loose" list
// that renders a paragraph gap after every bullet).
function tidyMarkdown(text) {
  return (text || "")
    .replace(/\r\n?/g, "\n")
    .replace(/[ \t ]+$/gm, "")
    .replace(/\n{3,}/g, "\n\n")
    .replace(/^(\s*(?:[-*+]|\d+[.)])\s.*)\n\n(?=\s*(?:[-*+]|\d+[.)])\s)/gm, "$1\n")
    .trim();
}

// The "[Ref N - url]" header refdata stores above the page text.
function stripRefHeader(text) {
  return (text || "").replace(/^\s*\[Ref [^\]\n]*\]\s*\n/, "");
}

function since(iso) {
  if (!iso) return "—";
  const mins = Math.max(0, (Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 60) return `${Math.round(mins)}m`;
  if (mins < 48 * 60) return `${Math.round(mins / 60)}h`;
  return `${Math.round(mins / 1440)}d`;
}

function SeverityBadge({ score }) {
  if (score == null) return <span className="jobs-muted">—</span>;
  const sev = severity(score);
  return (
    <span className="review-sev" style={{ background: SEVERITY_COLORS[sev] }} title={sev}>
      {Number(score).toFixed(1)}
    </span>
  );
}

const CWE_SOURCE = {
  CNA: "The CNA's own CWE (CVE record)",
  composite: "No CNA CWE: the models' majority vote",
  model: "No CNA CWE: the model's CWE",
};

// CWE ids linked to MITRE, with where they came from (CNA, else composite,
// else model; review_db.display_cwe).
function CweList({ cwe, source, compact }) {
  const ids = (cwe || "").split(";").filter(Boolean);
  if (!ids.length) return <span className="jobs-muted">{compact ? "—" : "no CWE"}</span>;
  return (
    <span className="review-cwe" title={CWE_SOURCE[source] || ""}>
      {ids.map((id) => {
        const n = id.match(/^CWE-(\d+)$/i)?.[1];
        return n ? (
          <a key={id} href={`https://cwe.mitre.org/data/definitions/${n}.html`} target="_blank" rel="noreferrer"
             onClick={(e) => e.stopPropagation()}>{id}</a>
        ) : <span key={id}>{id}</span>;
      })}
      {source && source !== "CNA" && <span className="review-cwe-src">{source}</span>}
    </span>
  );
}

function PrioBadge({ prio }) {
  if (!prio) return null;
  return <span className={`review-prio review-prio-${prio}`} title={PRIO_LABEL[prio]}>P{prio}</span>;
}

function ReasonChips({ codes, onClick }) {
  return (
    <span className="review-chips">
      {(codes || []).map((c) => (
        <span key={c} className="review-chip" onClick={onClick ? () => onClick(c) : undefined}>{c}</span>
      ))}
    </span>
  );
}

// Highlight whole-word, case-insensitive occurrences of the matched terms —
// the same matching the flagger uses (a hyphen counts as part of a word).
function Highlighted({ text, terms }) {
  const parts = useMemo(() => {
    const uniq = [...new Set((terms || []).filter(Boolean))].sort((a, b) => b.length - a.length);
    if (!text || !uniq.length) return [text || ""];
    const esc = uniq.map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, "\\$&").replace(/\s+/g, "\\s+"));
    const re = new RegExp(`(?<![\\w-])(${esc.join("|")})(?![\\w-])`, "gi");
    const out = [];
    let last = 0;
    for (const m of text.matchAll(re)) {
      if (m.index > last) out.push(text.slice(last, m.index));
      out.push(<mark key={m.index} className="review-hl">{m[0]}</mark>);
      last = m.index + m[0].length;
    }
    out.push(text.slice(last));
    return out;
  }, [text, terms]);
  return <>{parts}</>;
}

function MetricGrid({ cfg, final, raw, human, editable, value, onChange, accepted }) {
  const shown = editable ? value : human || final;
  return (
    <div className="review-metrics">
      {cfg.components.map((k) => {
        const v = shown?.[k];
        const rawDiff = raw && final && raw[k] !== final[k];
        const humanDiff = !editable && human && final && human[k] !== final[k];
        return (
          <div key={k} className={`review-metric${rawDiff ? " review-metric-changed" : ""}${humanDiff ? " review-metric-human" : ""}`}>
            <div className="review-metric-name">{cfg.names[k]} ({k})</div>
            {editable ? (
              <div className="review-metric-picker">
                {cfg.order[k].map((opt) => (
                  <button
                    key={opt}
                    type="button"
                    className={`review-opt${value[k] === opt ? " review-opt-on" : ""}${final?.[k] === opt ? " review-opt-model" : ""}`}
                    onClick={() => onChange({ ...value, [k]: opt })}
                    title={final?.[k] === opt ? "model's final value" : ""}
                  >
                    {cfg.values[k][opt]}
                  </button>
                ))}
              </div>
            ) : (
              <div className="review-metric-value">{v ? `${cfg.values[k][v]} (${v})` : "—"}</div>
            )}
            {rawDiff && (
              <div className="review-metric-note">
                model wrote {raw[k]}, {accepted ? "the vendor's score is" : "post-processing set"} {final[k]}
              </div>
            )}
            {humanDiff && <div className="review-metric-note">reviewer changed from {final[k]}</div>}
          </div>
        );
      })}
    </div>
  );
}

// ── Queue ─────────────────────────────────────────────────────────────

// Sortable columns: [sort key, header, first-click direction]. Keys match
// review_db.SORTS; null = not sortable.
function queueColumns(type) {
  const cfg = TYPES[type];
  return [
    ["cve", "CVE", "asc"],
    ...(type === "all" ? [["type", "Type", "asc"]] : []),
    ["cna", "CNA / vendor / product", "asc"],
    ["cwe", "CWE", "asc"],
    ["vector", cfg ? `Final ${cfg.unit}` : "Final vector / decision", "asc"],
    ["score", cfg ? cfg.scoreLabel : "Score / outcome", "desc"],
    ["priority", "Prio", "asc"],
    ["reasons", "Reasons", "desc"],
    ["confidence", "Conf.", "asc"],
    ["kernel", "Kernel", "desc"],
    ["scored", "Scored", "desc"],
  ];
}

function SortHeader({ col: [key, label, first], filters, setFilters }) {
  const on = filters.sort === key;
  const click = () => setFilters({ ...filters, sort: key, dir: on ? (filters.dir === "asc" ? "desc" : "asc") : first });
  return (
    <th className={`review-sortable${on ? " review-sorted" : ""}`} onClick={click}
        aria-sort={on ? (filters.dir === "asc" ? "ascending" : "descending") : "none"}
        title={on ? "Click to reverse" : `Sort by ${label}`}>
      {label}<span className="review-sort-arrow">{on ? (filters.dir === "asc" ? " ▲" : " ▼") : " ↕"}</span>
    </th>
  );
}

function QueueView({ filters, setFilters, onOpen, onRows }) {
  const type = filters.type;
  const typeLabel = TYPES[type]?.label || "CVSS + SSVC";
  const columns = queueColumns(type);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0);
  const [search, setSearch] = useState(filters.q);
  const perPage = 50;

  useEffect(() => setPage(0), [filters]);
  useEffect(() => {
    const t = setTimeout(() => setFilters((f) => (f.q === search ? f : { ...f, q: search })), 300);
    return () => clearTimeout(t);
  }, [search, setFilters]);

  useEffect(() => {
    let alive = true;
    setError(null);
    fetchReviewQueue(filters, page, perPage)
      .then((d) => {
        if (!alive) return;
        setData(d);
        onRows(d.rows.map((r) => ({ cve: r.cve_id, type: TYPE_OF[r.score_type] })));
      })
      .catch((e) => alive && setError(e.message));
    return () => { alive = false; };
  }, [filters, page, onRows]);

  const set = (k) => (e) => setFilters({ ...filters, [k]: e.target.value });
  const reasons = TYPES[type]?.reasons || ALL_REASONS;
  const setType = (e) => {
    const t = e.target.value;
    // Drop filters that mean nothing for the new type.
    setFilters({
      ...filters, type: t,
      reason: (TYPES[t]?.reasons || ALL_REASONS).includes(filters.reason) ? filters.reason : "",
      severity: t === "ssvc" ? "" : filters.severity,
      outcome: t === "cvss" ? "" : filters.outcome,
    });
  };
  const counts = data?.counts;
  const select = (k, opts) => (
    <select className="products-filter-select" value={filters[k]} onChange={set(k)}>
      {opts.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );

  return (
    <div>
      {counts && (
        <div className="review-counts">
          {[1, 2, 3].map((p) => (
            <button key={p} className="review-count" onClick={() => setFilters({ ...filters, priority: String(p) })}>
              <PrioBadge prio={p} /> <b>{counts.open_by_priority[p] || 0}</b> open
            </button>
          ))}
          <span className="review-count"><b>{counts.reviewed_today}</b> reviewed today</span>
          <span className="review-count" title={`A vendor (CNA) ${typeLabel} score is accepted as the score and never queued`}>
            <b>{counts.vendor_accepted}</b> vendor scores accepted
          </span>
          <span className="review-count-codes">
            {Object.entries(counts.open_by_reason).map(([code, n]) => (
              <span key={code} className="review-chip" onClick={() => setFilters({ ...filters, reason: code })}>
                {code} <b>{n}</b>
              </span>
            ))}
          </span>
        </div>
      )}

      <div className="filter-bar">
        <input type="text" placeholder="Search CVE ID or product" value={search}
               onChange={(e) => setSearch(e.target.value)} />
        <select className="products-filter-select" value={type} onChange={setType} aria-label="Score type">
          <option value="all">All score types</option>
          {Object.values(TYPES).map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
        </select>
        {select("priority", [["", "Any priority"], ["1", "P1"], ["2", "P2"], ["3", "P3"], ["1,2", "P1 + P2"]])}
        {select("reason", [["", "Any reason"], ...reasons.map((c) => [c, c])])}
        <input type="text" placeholder="CNA / vendor / product" value={filters.cna} onChange={set("cna")} />
        {select("kernel", [["", "Kernel + other"], ["yes", "Linux kernel"], ["no", "Non-kernel"]])}
        {select("confidence", [["", "Any confidence"], ["High", "High"], ["Medium", "Medium"], ["Low", "Low"]])}
        {type !== "ssvc" && select("severity", [["", "Any severity"], ["critical", "Critical"], ["high", "High"],
                                                ["medium", "Medium"], ["low", "Low"], ["none", "None"]])}
        {type !== "cvss" && select("outcome", [["", "Any outcome"], ...OUTCOMES.map((o) => [o, o])])}
        {select("status", [["unreviewed", "Unreviewed"], ["reviewed", "Reviewed"], ["all", "All flagged"]])}
        <button className="flush-btn" onClick={() => { setSearch(""); setFilters(DEFAULT_FILTERS); }}>Reset</button>
        <button className="raw-json-link" onClick={() => downloadReviewCsv(filters).catch((e) => setError(e.message))}>
          Export CSV
        </button>
      </div>

      {error && <div className="error">{error}</div>}
      <div className="panel">
        <div className="panel-header">
          <span>{typeLabel} review queue {data ? `— ${data.total.toLocaleString()} items` : ""}</span>
          {filters.sort && (
            <button className="raw-json-link" onClick={() => setFilters({ ...filters, sort: "", dir: "" })}>
              Default order
            </button>
          )}
        </div>
        <table className="products-table review-table">
          <thead>
            <tr>
              {columns.map((col) => <SortHeader key={col[0]} col={col} filters={filters} setFilters={setFilters} />)}
            </tr>
          </thead>
          <tbody>
            {data?.rows.map((r) => {
              const cfg = TYPES[TYPE_OF[r.score_type]];
              return (
              <tr key={`${r.score_type}/${r.cve_id}`} className="products-row review-row" onClick={() => onOpen(r.cve_id, cfg.key)}>
                <td className="review-cve">{r.cve_id}{r.reviewed_by && <div className="jobs-muted">✓ {r.reviewed_by}</div>}</td>
                {type === "all" && <td><span className={`review-chip review-type-chip-${cfg.key}`}>{cfg.label}</span></td>}
                <td>
                  <div>{r.cna}</div>
                  <div className="jobs-muted">{[r.vendor, r.product].filter(Boolean).join(" / ")}</div>
                </td>
                <td><CweList cwe={r.cwe} source={r.cwe_source} compact /></td>
                <td className="review-vector">{(r.final_vector || "—").replace(cfg.prefix, "")}</td>
                <td><cfg.Badge vector={r.final_vector} /></td>
                <td><PrioBadge prio={r.review_priority} /></td>
                <td><ReasonChips codes={r.review_reasons} /></td>
                <td>{r.confidence || "—"}</td>
                <td>{r.kernel ? "yes" : "no"}</td>
                <td className="jobs-muted" title={r.scored_at}>{since(r.scored_at)} ago</td>
              </tr>
              );
            })}
            {data && !data.rows.length && (
              <tr><td colSpan={columns.length} className="jobs-muted">Nothing matches these filters.</td></tr>
            )}
          </tbody>
        </table>
        {data && data.total > perPage && (
          <div className="review-pager">
            <button className="flush-btn" disabled={page === 0} onClick={() => setPage(page - 1)}>Previous</button>
            <span className="jobs-muted">Page {page + 1} of {Math.ceil(data.total / perPage)}</span>
            <button className="flush-btn" disabled={(page + 1) * perPage >= data.total} onClick={() => setPage(page + 1)}>
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Detail ────────────────────────────────────────────────────────────

function DetailView({ cfg, cveId, filters, queueIds, onNavigate, onBack }) {
  const [d, setD] = useState(null);
  const [error, setError] = useState(null);
  const [lock, setLock] = useState(null);
  const [mode, setMode] = useState(null); // null | "fix"
  const [picked, setPicked] = useState(null);
  const [note, setNote] = useState("");
  const [ruleProblem, setRuleProblem] = useState(false);
  const [ruleNote, setRuleNote] = useState("");
  const [showRationale, setShowRationale] = useState(false);
  const [saving, setSaving] = useState(false);
  const [conflict, setConflict] = useState(null);
  const [message, setMessage] = useState(null);

  const load = useCallback(() => {
    setD(null); setError(null); setMode(null); setNote(""); setRuleProblem(false); setRuleNote("");
    setConflict(null); setMessage(null); setLock(null);
    // type = this record's score type; queue_type = the queue it was opened
    // from, so prev/next walk the same (possibly mixed-type) list.
    fetchReview(cveId, { ...filters, type: cfg.key, queue_type: filters.type })
      .then((data) => {
        setD(data);
        setPicked(cfg.parse(data.review.human_vector || data.review.final_vector));
        if (data.can_review) lockReview(cveId, cfg.key).then(setLock).catch(() => {});
      })
      .catch((e) => setError(e.message));
  }, [cfg, cveId, filters]);
  useEffect(load, [load]);

  // Prev/next are {cve, type}: the current page of the queue first, else the
  // server's neighbours (opened from a link, or at a page boundary).
  const idx = queueIds.findIndex((q) => q.cve === cveId && q.type === cfg.key);
  const asItem = (n) => (n ? { cve: n.cve_id, type: TYPE_OF[n.score_type] } : null);
  const prevId = (idx > 0 ? queueIds[idx - 1] : null) || asItem(d?.neighbours?.prev);
  const nextId = (idx >= 0 ? queueIds[idx + 1] : null) || asItem(d?.neighbours?.next);

  const final = d?.metrics?.final_vector;
  const changed = picked && final && cfg.format(picked) !== cfg.format(final);

  const save = useCallback(async (decision, force = false) => {
    if (!d?.can_review || saving) return;
    if (decision === "fix_vector" && !changed) { setMessage(`Change at least one ${cfg.key === "ssvc" ? "decision point" : "metric"}, or use Agree.`); return; }
    if (decision === "fix_vector" && !note.trim()) { setMessage("A note is required when you change the vector."); return; }
    setSaving(true); setMessage(null);
    const r = await saveReviewDecision(cveId, {
      type: cfg.key,
      decision,
      human_vector: decision === "fix_vector" ? cfg.format(picked) : null,
      note, rule_problem: ruleProblem, rule_note: ruleNote,
      expected_reviewed_at: d.review.reviewed_at, force,
    }).catch((e) => ({ status: 0, error: e.message }));
    setSaving(false);
    if (r.status === 409) { setConflict({ ...r, decision }); return; }
    if (!r.ok) { setMessage(r.error || "Save failed"); return; }
    if (nextId) onNavigate(nextId); else onBack();
  }, [cfg, d, saving, changed, note, cveId, picked, ruleProblem, ruleNote, nextId, onNavigate, onBack]);

  useEffect(() => {
    const onKey = (e) => {
      if (["INPUT", "TEXTAREA", "SELECT"].includes(e.target.tagName) || e.metaKey || e.ctrlKey || e.altKey) return;
      if (e.key === "a" && d?.can_review) { e.preventDefault(); save("agree"); }
      else if (e.key === "f" && d?.can_review) { e.preventDefault(); setMode("fix"); }
      else if (e.key === "n" && nextId) onNavigate(nextId);
      else if (e.key === "p" && prevId) onNavigate(prevId);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [d, save, nextId, prevId, onNavigate]);

  if (error) return <div className="error">{error} <button className="flush-btn" onClick={onBack}>Back</button></div>;
  if (!d) return <div className="loading">Loading {cveId}…</div>;

  const r = d.review;
  const ev = r.review_evidence || {};
  const terms = (field) => Object.values(ev).flat().flatMap((i) => i.matches || [])
    .filter((m) => m.field === field).map((m) => m.text);

  return (
    <div className="review-detail">
      <div className="review-nav">
        <button className="flush-btn" onClick={onBack}>← Queue</button>
        <button className="flush-btn" disabled={!prevId} onClick={() => onNavigate(prevId)}>Previous (p)</button>
        <button className="flush-btn" disabled={!nextId} onClick={() => onNavigate(nextId)}>Skip (n)</button>
        <h2 className="review-title">{r.cve_id}</h2>
        <PrioBadge prio={r.review_priority} />
        <span className="jobs-muted">{[r.cna, r.vendor, r.product].filter(Boolean).join(" · ")}</span>
        <CweList cwe={r.cwe} source={r.cwe_source} />
        <span className="jobs-muted">Kernel CVE: {r.kernel ? "yes" : "no"}</span>
        <span className="review-chip">{cfg.label}</span>
      </div>

      {lock && !lock.ok && lock.locked_by && (
        <div className="review-warning">{lock.locked_by} has this CVE open (since {fmtTs(lock.locked_at)}).</div>
      )}
      {r.accepted_source && (
        <div className="review-info">
          The vendor (CNA) published its own {cfg.label === "CVSS 3.1" ? "CVSS" : cfg.label} score, accepted as the
          score and not queued for review.
          {r.raw_vector && r.raw_vector !== r.final_vector && ` The model scored ${r.raw_vector.replace(cfg.prefix, "")}.`}
        </div>
      )}
      {r.reviewed_at && (
        <div className="review-info">
          Reviewed by <b>{r.reviewed_by}</b> {fmtTs(r.reviewed_at)} — {r.human_decision}
          {r.human_note ? `: ${r.human_note}` : ""}.
          {r.reviewed_final_vector && r.reviewed_final_vector !== r.final_vector &&
            " Rescored since review: the model's final vector has changed."}
        </div>
      )}

      <div className="review-grid">
        <div>
          <div className="panel">
            <div className="panel-header"><span>Why this is flagged</span></div>
            <div className="panel-body">
              {r.review_reasons.map((code) => (
                <div key={code} className="review-reason">
                  <span className="review-chip">{code}</span>
                  {(ev[code] || []).map((item, i) => (
                    <div key={i} className="review-reason-text">
                      {item.label}
                      {item.matches?.length > 0 && (
                        <span className="jobs-muted"> — matched {[...new Set(item.matches.map((m) => `"${m.text}"`))].join(", ")} in the {item.matches[0].field}</span>
                      )}
                    </div>
                  ))}
                </div>
              ))}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header"><span>Description</span></div>
            <div className="panel-body review-text"><Highlighted text={d.description} terms={terms("description")} /></div>
          </div>

          <CweBox cwe={d.cwe} />

          <div className="panel">
            <div className="panel-header">
              <span>Model rationale</span>
              <button className="raw-json-link" onClick={() => setShowRationale(!showRationale)}>
                {showRationale ? "Collapse" : "Expand"}
              </button>
            </div>
            <div className="panel-body jobs-muted review-meta">
              {r.bug_class && <>Bug class: K4{r.bug_class} · </>}
              Confidence: {r.confidence || "—"} · scored {fmtTs(r.scored_at)} · model {r.model}
              <div title={r.prompt_sha256 || ""}>prompt_sha256: {(r.prompt_sha256 || "unknown").slice(0, 16)}…</div>
            </div>
            {showRationale && (
              <pre className="panel-body review-rationale">
                <Highlighted text={tidyMarkdown(d.rationale)} terms={terms("rationale")} />
              </pre>
            )}
          </div>

          <ReferenceViewer refs={d.references} />
        </div>

        <div>
          <div className="panel">
            <div className="panel-header">
              <span>{r.human_vector ? `Published (reviewer) ${cfg.unit}` : `Final ${cfg.unit}`}</span>
              <span className="review-score"><cfg.Badge vector={r.human_vector || r.final_vector} /></span>
            </div>
            <div className="panel-body">
              <div className="review-vector">{(r.human_vector || r.final_vector || `no valid ${cfg.unit}`).replace(cfg.prefix, "")}</div>
              {final && <MetricGrid cfg={cfg} final={final} raw={d.metrics.raw_vector} human={d.metrics.human_vector}
                                    accepted={!!r.accepted_source} />}
            </div>
          </div>

          <div className="panel">
            <div className="panel-header"><span>Decision</span></div>
            <div className="panel-body">
              {r.accepted_source && (
                <div className="jobs-muted">No review needed: the vendor's score is published. A decision recorded here
                  takes precedence over it.</div>
              )}
              {!d.can_review && <div className="jobs-muted">Only reviewers can record a decision.</div>}
              {d.can_review && (
                <>
                  <div className="review-actions">
                    <button className="analyze-btn composite-btn" disabled={saving || !final} onClick={() => save("agree")}>
                      Agree &amp; next (a)
                    </button>
                    <button className="analyze-btn" disabled={saving} onClick={() => setMode(mode === "fix" ? null : "fix")}>
                      Fix {cfg.unit} (f)
                    </button>
                  </div>
                  {mode === "fix" && picked && (
                    <div className="review-fix">
                      <div className="review-fix-score">
                        New {cfg.scoreLabel.toLowerCase()}: <cfg.Badge vector={cfg.format(picked)} /> <span className="review-vector">{cfg.format(picked).replace(cfg.prefix, "")}</span>
                      </div>
                      <MetricGrid cfg={cfg} final={final} editable value={picked} onChange={setPicked} />
                    </div>
                  )}
                  <textarea className="review-note" placeholder={mode === "fix" ? `Note (required when the ${cfg.unit} changes)` : "Note (optional)"}
                            value={note} onChange={(e) => setNote(e.target.value)} />
                  <label className="review-check">
                    <input type="checkbox" checked={ruleProblem} onChange={(e) => setRuleProblem(e.target.checked)} />
                    Rule problem — the prompt rule itself is wrong
                  </label>
                  {ruleProblem && (
                    <textarea className="review-note" placeholder="Which rule, and what it should say"
                              value={ruleNote} onChange={(e) => setRuleNote(e.target.value)} />
                  )}
                  {mode === "fix" && (
                    <button className="analyze-btn composite-btn" disabled={saving} onClick={() => save("fix_vector")}>
                      Save fix &amp; next
                    </button>
                  )}
                  {message && <div className="review-warning">{message}</div>}
                  {conflict && (
                    <div className="review-warning">
                      {conflict.error}. Saving now replaces their decision.
                      <div className="review-actions">
                        <button className="flush-btn" onClick={() => save(conflict.decision, true)}>Overwrite</button>
                        <button className="flush-btn" onClick={load}>Reload</button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ── CWE ───────────────────────────────────────────────────────────────

const CWE_SOURCE_LABEL = { CNA: "from the CNA", composite: "models' majority vote", model: "model's CWE" };

function CweEntry({ e }) {
  if (!e.known) {
    return <div className="review-cwe-entry"><b className="review-cwe-id">{e.id}</b>
      <span className="jobs-muted"> — not in the MITRE CWE catalog</span></div>;
  }
  return (
    <div className="review-cwe-entry">
      <div className="review-cwe-title">
        {e.url ? <a href={e.url} target="_blank" rel="noreferrer" className="review-cwe-id">{e.id}</a>
          : <b className="review-cwe-id">{e.id}</b>}
        <span className="review-cwe-name">{e.name}</span>
        {(e.abstraction || e.kind) && <span className="review-chip">{e.abstraction || e.kind}</span>}
        {["Deprecated", "Obsolete"].includes(e.status) && <span className="review-chip review-chip-warn">{e.status}</span>}
      </div>
      {e.description && <div className="review-cwe-desc">{e.description}</div>}
      {e.chain.length > 1 && (
        <ol className="review-cwe-tree" aria-label="Hierarchy (Research view, CWE-1000)">
          {e.chain.map((c, i) => (
            <li key={c.id} style={{ paddingLeft: i * 16 }} className={i === e.chain.length - 1 ? "review-cwe-self" : ""}>
              {i > 0 && <span className="review-cwe-branch">└ </span>}
              <a href={`https://cwe.mitre.org/data/definitions/${c.id.slice(4)}.html`} target="_blank"
                 rel="noreferrer">{c.id}</a> {c.name}
              {c.abstraction && <span className="jobs-muted"> · {c.abstraction}</span>}
            </li>
          ))}
        </ol>
      )}
      {e.other_parents?.length > 0 && (
        <div className="jobs-muted review-cwe-desc">Also a child of {e.other_parents.join(", ")}</div>
      )}
    </div>
  );
}

function CweBox({ cwe }) {
  if (!cwe) return null;
  const shown = new Set(cwe.entries.map((e) => e.id));
  return (
    <div className="panel">
      <div className="panel-header">
        <span>CWE {cwe.source && <span className="review-cwe-src">{CWE_SOURCE_LABEL[cwe.source] || cwe.source}</span>}</span>
        <span className="jobs-muted" title="Names and hierarchy from MITRE's catalog, Research view (CWE-1000)">
          MITRE CWE {cwe.catalog_version}
        </span>
      </div>
      <div className="panel-body">
        {cwe.entries.length ? cwe.entries.map((e) => <CweEntry key={e.id} e={e} />)
          : <span className="jobs-muted">No CWE from the CNA or the models.</span>}
        {cwe.vote && <div className="jobs-muted review-cwe-desc">{cwe.vote}</div>}
      </div>
      {cwe.rationales.length > 0 && (
        <div className="panel-body review-cwe-rationales">
          <div className="review-metric-name">Model rationale for the CWE</div>
          {cwe.rationales.map((r, i) => (
            <details key={r.model} open={i === 0} className="review-cwe-rationale">
              <summary>
                <b>{r.model}</b> → {r.cwe || "no CWE"}
                {r.confidence && <span className="jobs-muted"> · {r.confidence} confidence</span>}
                {r.cwe && shown.size > 0 && (
                  <span className={shown.has(r.cwe) ? "review-cwe-agree" : "review-cwe-differ"}>
                    {shown.has(r.cwe) ? " ✓ same CWE" : " ≠ differs"}
                  </span>
                )}
                {r.timestamp && <span className="jobs-muted"> · {fmtTs(r.timestamp)}</span>}
              </summary>
              <div className="review-text review-cwe-text">
                {tidyMarkdown(r.rationale)}
              </div>
            </details>
          ))}
        </div>
      )}
    </div>
  );
}

// ── References ────────────────────────────────────────────────────────

// Links open in a new tab; images are shown as their alt text (scraped pages
// are full of badges and trackers, and the text is what the scorer read).
const MD_COMPONENTS = {
  a: ({ node, children, ...props }) => <a {...props} target="_blank" rel="noreferrer">{children}</a>,
  img: ({ alt }) => (alt ? <span className="jobs-muted">[image: {alt}]</span> : null),
};

const REF_STATUS = {
  cached: "Cached text is available",
  unavailable: "Fetched, but no text could be extracted (PDF, login wall, script-only page…)",
  missing: "Never fetched",
};

function ReferenceItem({ ref_ }) {
  const cached = ref_.status === "cached";
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState(null); // null = not loaded, "" = loading
  const [error, setError] = useState(null);
  const toggle = () => {
    if (!open && content === null) {
      setContent("");
      setError(null);
      fetchRefContent(ref_.hash)
        .then((r) => setContent(tidyMarkdown(stripRefHeader(r.content))))
        .catch(() => { setContent(null); setError("Could not load the cached text — open the link instead."); });
    }
    setOpen(!open);
  };
  return (
    <div className="review-ref">
      <div className="review-ref-head">
        <button className="review-ref-toggle" onClick={toggle} aria-expanded={open} disabled={!cached}
                title={cached ? "Show the cached text" : REF_STATUS[ref_.status]}>
          {cached ? (open ? "▾" : "▸") : "·"}
        </button>
        <a href={ref_.url} target="_blank" rel="noreferrer" className="review-ref-url">{ref_.url}</a>
        <span className={`review-ref-status ${cached ? "review-ref-cached" : ""}`} title={REF_STATUS[ref_.status]}>
          {ref_.status === "missing" ? "not cached" : ref_.status}
        </span>
        {ref_.tags.map((t) => <span key={t} className="review-chip">{t}</span>)}
      </div>
      {open && (
        <div className="review-ref-body">
          {error ? <span className="jobs-muted">{error}</span>
            : content === "" || content === null ? <span className="jobs-muted">Loading…</span>
            : (
              <div className="review-ref-md">
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={MD_COMPONENTS}>{content}</ReactMarkdown>
              </div>
            )}
        </div>
      )}
    </div>
  );
}

function ReferenceViewer({ refs }) {
  const cached = refs.filter((r) => r.status === "cached").length;
  return (
    <div className="panel">
      <div className="panel-header">
        <span>References</span>
        <span className="jobs-muted">{refs.length} total · {cached} cached</span>
      </div>
      <div className="panel-body review-refs">
        {refs.length ? refs.map((r) => <ReferenceItem key={r.url} ref_={r} />)
          : <span className="jobs-muted">No references in the CVE record.</span>}
      </div>
    </div>
  );
}

// ── Feedback ──────────────────────────────────────────────────────────

function FeedbackView({ cfg }) {
  const [fb, setFb] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { fetchReviewFeedback(cfg.key).then(setFb).catch((e) => setError(e.message)); }, [cfg]);
  if (error) return <div className="error">{error}</div>;
  if (!fb) return <div className="loading">Loading…</div>;
  const ra = fb.random_audit;
  return (
    <div>
      <div className="panel">
        <div className="panel-header"><span>{cfg.label}: per reason code — {fb.reviewed} reviewed</span></div>
        <table className="products-table">
          <thead><tr><th>Reason</th><th>Reviewed</th><th>Agree</th><th>Fixed {cfg.unit}</th><th>Rule problem</th></tr></thead>
          <tbody>
            {Object.entries(fb.per_code).map(([code, s]) => (
              <tr key={code}>
                <td><span className="review-chip">{code}</span></td>
                <td>{s.reviewed}</td><td>{s.agree_pct}%</td><td>{s.fix_vector_pct}%</td><td>{s.rule_problem_pct}%</td>
              </tr>
            ))}
            {!Object.keys(fb.per_code).length && <tr><td colSpan={5} className="jobs-muted">No reviews yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="panel">
        <div className="panel-header"><span>Error rate in unflagged output (RANDOM_AUDIT)</span></div>
        <div className="panel-body">
          {ra.reviewed ? (
            <>
              <b>{(100 * ra.error_rate).toFixed(1)}%</b> of {ra.reviewed} audited rows needed a {cfg.unit} fix
              <span className="jobs-muted"> (95% CI {(100 * ra.ci95[0]).toFixed(1)}–{(100 * ra.ci95[1]).toFixed(1)}%)</span>
            </>
          ) : <span className="jobs-muted">No RANDOM_AUDIT rows reviewed yet.</span>}
        </div>
      </div>
      <div className="panel">
        <div className="panel-header"><span>{cfg.key === "ssvc" ? "Decision points" : "Metrics"} reviewers change most</span></div>
        <table className="products-table">
          <thead><tr><th>{cfg.key === "ssvc" ? "Decision point" : "Metric"}</th><th>Changes</th><th>Transitions</th></tr></thead>
          <tbody>
            {Object.entries(fb.metric_changes).filter(([, v]) => v.total).map(([k, v]) => (
              <tr key={k}>
                <td>{cfg.names[k]} ({k})</td><td>{v.total}</td>
                <td className="jobs-muted">{Object.entries(v.transitions).map(([t, n]) => `${t} ×${n}`).join(", ")}</td>
              </tr>
            ))}
            {!Object.values(fb.metric_changes).some((v) => v.total) &&
              <tr><td colSpan={3} className="jobs-muted">No {cfg.unit} changes yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ── Rules ─────────────────────────────────────────────────────────────

// One line of TOML: comments muted, [section] headers and keys highlighted.
function TomlLine({ line }) {
  const trimmed = line.trim();
  if (trimmed.startsWith("#")) return <span className="toml-comment">{line}</span>;
  if (/^\[\[?[\w.]+\]\]?$/.test(trimmed)) return <span className="toml-section">{line}</span>;
  const m = line.match(/^(\s*)([\w.-]+)(\s*=)(.*)$/);
  if (m) return <>{m[1]}<span className="toml-key">{m[2]}</span>{m[3]}{m[4]}</>;
  return <>{line}</>;
}

function RulesView({ cfg }) {
  const [rules, setRules] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => { setRules(null); fetchReviewRules(cfg.key).then(setRules).catch((e) => setError(e.message)); }, [cfg]);
  if (error) return <div className="error">{error}</div>;
  if (!rules) return <div className="loading">Loading…</div>;
  return (
    <div className="panel review-rules">
      <div className="panel-header">
        <span>{rules.path}</span>
        <span className="jobs-muted review-rules-meta">
          read-only · modified {fmtTs(rules.modified)} · <span title={rules.sha256}>sha256 {rules.sha256.slice(0, 12)}…</span>
        </span>
      </div>
      <div className="panel-body jobs-muted review-rules-note">
        The rules the flagger applies to every new score. Changing them means editing this file in the repo and
        deploying; existing flags keep the old rules until <code>scripts/review-flags.py run</code> re-flags them.
      </div>
      <pre className="panel-body review-toml">
        {rules.text.split("\n").map((line, i) => (
          <div key={i} className="toml-line"><span className="toml-ln">{i + 1}</span><TomlLine line={line} /></div>
        ))}
      </pre>
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────

export default function ReviewPanel() {
  const [tab, setTab] = useState("queue");
  // loc.type: the open record's type (cvss | ssvc) when loc.cve is set, else
  // the queue's type filter as in the URL (all | cvss | ssvc).
  const [{ type, cve: cveId }, setLoc] = useState(fromPath);
  const [filters, setFiltersState] = useState(() => ({ ...DEFAULT_FILTERS, type: fromPath().cve ? "all" : fromPath().type }));
  // Feedback and Rules are per score type; their own switch.
  const [infoType, setInfoType] = useState(type === "ssvc" ? "ssvc" : "cvss");
  const queueIds = useRef([]);
  const [, bump] = useState(0);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;

  useEffect(() => {
    const onPop = () => {
      const loc = fromPath();
      setLoc(loc);
      if (!loc.cve) setFiltersState((f) => (f.type === loc.type ? f : { ...f, type: loc.type }));
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const go = useCallback((nextType, cve) => {
    window.history.pushState(null, "", reviewPath(nextType, cve));
    setLoc({ type: nextType, cve });
  }, []);
  // Keep the queue URL in step with the type filter (/review, /review/ssvc …).
  const setFilters = useCallback((next) => {
    const prev = filtersRef.current;
    const f = typeof next === "function" ? next(prev) : next;
    if (f === prev) return;
    filtersRef.current = f;
    setFiltersState(f);
    if (f.type !== prev.type && !fromPath().cve) {
      window.history.replaceState(null, "", reviewPath(f.type, null));
      setLoc({ type: f.type, cve: null });
    }
  }, []);
  const open = useCallback((id, rowType) => go(rowType, id), [go]);
  const navigate = useCallback((item) => go(item.type, item.cve), [go]);
  const back = useCallback(() => go(filtersRef.current.type, null), [go]);
  const onRows = useCallback((ids) => { queueIds.current = ids; bump((n) => n + 1); }, []);
  const infoCfg = TYPES[infoType];

  return (
    <div>
      <div className="table-tabs review-tabs">
        {[["queue", "Queue"], ["feedback", "Feedback"], ["rules", "Rules"]].map(([key, label]) => (
          <button key={key} className={`table-tab ${tab === key ? "active" : ""}`}
                  onClick={() => { setTab(key); if (key === "queue" && cveId) back(); }}>
            {label}
          </button>
        ))}
        {tab !== "queue" && (
          <span className="review-type-switch">
            {Object.values(TYPES).map((t) => (
              <button key={t.key} className={`review-type${infoType === t.key ? " review-type-on" : ""}`}
                      onClick={() => setInfoType(t.key)}>
                {t.label}
              </button>
            ))}
          </span>
        )}
      </div>
      {tab === "feedback" && <FeedbackView cfg={infoCfg} />}
      {tab === "rules" && <RulesView cfg={infoCfg} />}
      {tab === "queue" && !cveId && (
        <QueueView filters={filters} setFilters={setFilters} onOpen={open} onRows={onRows} />
      )}
      {tab === "queue" && cveId && (
        <DetailView key={`${type}/${cveId}`} cfg={TYPES[type]} cveId={cveId} filters={filters}
                    queueIds={queueIds.current} onNavigate={navigate} onBack={back} />
      )}
    </div>
  );
}
